import axios from 'axios'

export interface Payment {
  payment_id: string
  reservation_id: string
  user_id: string
  amount: number
  currency: string
  payment_method: string
  payment_status: 'pending' | 'completed' | 'failed' | 'refunded'
  transaction_id: string | null
  payment_gateway_response: any | null
  created_at: string
  updated_at: string
  user_first_name: string | null
  user_last_name: string | null
  reservation_status: string | null
}

export interface CreatePaymentDto {
  reservation_id: string
  user_id: string
  amount: number
  currency?: string
  payment_method: string
}

export interface UpdatePaymentDto {
  amount?: number
  currency?: string
  payment_method?: string
}

export interface PaginatedResponse<T> {
  data: T[]
  total: number
  success: boolean
}

export interface PaymentFilters {
  user_id?: string
  reservation_id?: string
  payment_status?: string
  payment_method?: string
}

// Use proxy configuration from vite.config.ts
const API_BASE_URL = '/api'

const paymentApi = axios.create({
  baseURL: `${API_BASE_URL}/payments`,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add request interceptor for authentication
paymentApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Add response interceptor for error handling
paymentApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('access_token')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

export const paymentService = {
  // Get all payments with optional filters
  async getAll(filters: PaymentFilters = {}): Promise<PaginatedResponse<Payment>> {
    const params = new URLSearchParams()
    
    if (filters.user_id) params.append('user_id', filters.user_id)
    if (filters.reservation_id) params.append('reservation_id', filters.reservation_id)
    if (filters.payment_status) params.append('payment_status', filters.payment_status)
    if (filters.payment_method) params.append('payment_method', filters.payment_method)

    const response = await paymentApi.get('', { params })
    return response.data
  },

  // Get payment by ID
  async getById(id: string): Promise<{ success: boolean; data: Payment }> {
    const response = await paymentApi.get(`/${id}`)
    return response.data
  },

  // Create new payment
  async create(paymentData: CreatePaymentDto): Promise<{ success: boolean; data: Payment; message: string }> {
    const response = await paymentApi.post('', paymentData)
    return response.data
  },

  // Update payment
  async update(id: string, paymentData: UpdatePaymentDto): Promise<{ success: boolean; data: Payment; message: string }> {
    const response = await paymentApi.put(`/${id}`, paymentData)
    return response.data
  },

  // Update payment status
  async updateStatus(id: string, payment_status: 'pending' | 'completed' | 'failed' | 'refunded', transaction_id?: string, payment_gateway_response?: any): Promise<{ success: boolean; data: Payment; message: string }> {
    const response = await paymentApi.patch(`/${id}/status`, { 
      payment_status, 
      transaction_id, 
      payment_gateway_response 
    })
    return response.data
  },

  // Process payment (mock implementation)
  async process(id: string, payment_method_details?: any): Promise<{ success: boolean; data: Payment; message: string }> {
    const response = await paymentApi.post(`/${id}/process`, { payment_method_details })
    return response.data
  },

  // Refund payment
  async refund(id: string, refund_amount?: number): Promise<{ success: boolean; data: Payment; message: string }> {
    const response = await paymentApi.post(`/${id}/refund`, { refund_amount })
    return response.data
  },

  // Get payment statistics
  async getStatistics(): Promise<{
    total: number
    totalAmount: number
    byStatus: Record<string, number>
    byMethod: Record<string, number>
    averageAmount: number
    refundedAmount: number
  }> {
    const allPayments = await this.getAll()
    const payments = allPayments.data
    
    const byStatus: Record<string, number> = {}
    const byMethod: Record<string, number> = {}
    
    payments.forEach(payment => {
      byStatus[payment.payment_status] = (byStatus[payment.payment_status] || 0) + 1
      byMethod[payment.payment_method] = (byMethod[payment.payment_method] || 0) + 1
    })
    
    const completedPayments = payments.filter(p => p.payment_status === 'completed')
    const refundedPayments = payments.filter(p => p.payment_status === 'refunded')
    
    const stats = {
      total: payments.length,
      totalAmount: completedPayments.reduce((sum, p) => sum + p.amount, 0),
      byStatus,
      byMethod,
      averageAmount: completedPayments.length > 0 
        ? completedPayments.reduce((sum, p) => sum + p.amount, 0) / completedPayments.length
        : 0,
      refundedAmount: refundedPayments.reduce((sum, p) => sum + p.amount, 0)
    }
    
    return stats
  }
}

export default paymentService
